import React, { useState } from 'react';

export default function AnalysisCanvas() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div style={{ fontFamily: 'monospace', padding: '20px', backgroundColor: '#0d1117', color: '#c9d1d9', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #30363d', paddingBottom: '20px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#58a6ff' }}>Quick Order Feature Analysis</h1>
        <p style={{ margin: '5px 0', color: '#8b949e' }}>Price Mapping Issue & Code Redundancy Report</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', borderBottom: '1px solid #30363d', paddingBottom: '15px' }}>
        {['overview', 'bug', 'redundancy', 'heatmap', 'fixes'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 16px',
              background: activeTab === tab ? '#238636' : 'transparent',
              color: activeTab === tab ? '#fff' : '#8b949e',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              fontSize: '12px',
              textTransform: 'uppercase'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          <h2 style={{ color: '#79c0ff', marginBottom: '20px' }}>🎯 Executive Summary</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '30px' }}>
            <MetricBox title="Bug Severity" value="CRITICAL" color="#f85149" />
            <MetricBox title="Duplication" value="78%" color="#d29922" />
            <MetricBox title="Affected Files" value="4" color="#a371f7" />
            <MetricBox title="Redundant Lines" value="350+" color="#fb8500" />
          </div>

          <h3 style={{ color: '#79c0ff', marginTop: '30px' }}>📊 Impact Overview</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #30363d' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>Component</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>Issue</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>User Impact</th>
              </tr>
            </thead>
            <tbody>
              <TableRow comp="Order Load" issue="Price swap on edit" impact="Wrong pricing displayed" />
              <TableRow comp="Qty Update" issue="3x duplicated logic" impact="Maintenance risk" />
              <TableRow comp="Discount" issue="4x calc duplication" impact="Inconsistency risk" />
              <TableRow comp="Promotion" issue="3x API logic repeated" impact="Code debt" />
            </tbody>
          </table>
        </div>
      )}

      {/* Bug Analysis Tab */}
      {activeTab === 'bug' && (
        <div>
          <h2 style={{ color: '#f85149', marginBottom: '20px' }}>🐛 Price Mapping Bug Details</h2>
          
          <div style={{ 
            backgroundColor: '#161b22', 
            padding: '20px', 
            borderRadius: '6px', 
            marginBottom: '20px',
            border: '1px solid #30363d'
          }}>
            <h3 style={{ color: '#79c0ff', margin: '0 0 15px 0' }}>Root Cause Location</h3>
            <p style={{ margin: '0 0 10px 0', color: '#8b949e' }}><strong>File:</strong> src/components/sales/Incomplete_Order.js</p>
            <p style={{ margin: '0 0 10px 0', color: '#8b949e' }}><strong>Lines:</strong> 54-109 (handleEdit function)</p>
            <p style={{ margin: '0 0 10px 0', color: '#8b949e' }}><strong>Bug Lines:</strong> 64-70 (pdtcost assignment) & 97 (field mapping)</p>
          </div>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>The Bug Mechanism</h3>
          <div style={{ 
            backgroundColor: '#0d1117', 
            padding: '15px', 
            borderRadius: '6px',
            marginBottom: '20px',
            border: '2px solid #f85149'
          }}>
            <p style={{ margin: '0 0 10px 0' }}><span style={{ color: '#79c0ff' }}>Step 1:</span> Backend returns productData with price & piece_cost</p>
            <p style={{ margin: '0 0 10px 0' }}><span style={{ color: '#79c0ff' }}>Step 2:</span> Code checks discount flag for price/piece_cost fields ✓</p>
            <p style={{ margin: '0 0 10px 0' }}><span style={{ color: '#f85149' }}>Step 3:</span> <strong>BUG:</strong> pdtcost assignment SKIPS discount check ✗</p>
            <p style={{ margin: '0 0 10px 0' }}><span style={{ color: '#79c0ff' }}>Step 4:</span> UI calculates using pdtcost with discount</p>
            <p style={{ margin: '0' }}><span style={{ color: '#f85149' }}>Result:</span> <strong>Price fields don't match calculations → User sees swapped prices</strong></p>
          </div>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>❌ Buggy Code</h3>
          <CodeBlock code={`// Lines 64-70 in Incomplete_Order.js
let pdtcost;
if(productData.order_type=='piece') {
  pdtcost = productData.piece_cost;    // ❌ Missing discount check!
} else {        
  pdtcost = productData.price;         // ❌ Missing discount check!
}`} />

          <h3 style={{ color: '#79c0ff', marginBottom: '15px', marginTop: '20px' }}>✅ Fixed Code</h3>
          <CodeBlock code={`// Corrected assignment (add after line 70)
const pdtcost = productData.productDiscountApplicable === "1"
  ? (productData.order_type === 'piece' 
      ? parseFloat(productData.discountedSplitPrice)
      : parseFloat(productData.discountedBoxPrice))
  : (productData.order_type === 'piece'
      ? parseFloat(productData.piece_cost)
      : parseFloat(productData.price));`} />
        </div>
      )}

      {/* Redundancy Tab */}
      {activeTab === 'redundancy' && (
        <div>
          <h2 style={{ color: '#a371f7', marginBottom: '20px' }}>🔄 Code Redundancy Analysis</h2>
          
          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>Redundancy #1: Price Calculation (4 instances)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #30363d' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff', width: '30%' }}>File</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff', width: '20%' }}>Lines</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>Function</th>
              </tr>
            </thead>
            <tbody>
              <TableRow comp="Incomplete_Order.js" issue="71-74" impact="Order load calc" />
              <TableRow comp="Quick_Order.js" issue="300-305" impact="Qty change calc" />
              <TableRow comp="Quick_Order.js" issue="491-492" impact="Option change calc" />
              <TableRow comp="EposServices.js" issue="99-106" impact="Product add calc" />
            </tbody>
          </table>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>Redundancy #2: Promotion Check Logic (3 instances)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '30px' }}>
            <RedundancyBox file="EposServices.js" lines="149-287" size="164 lines" />
            <RedundancyBox file="Quick_Order.js" lines="342-452" size="137 lines" />
            <RedundancyBox file="Quick_Order.js" lines="557-650+" size="118+ lines" />
          </div>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>Redundancy #3: Discount & VAT (2+ instances)</h3>
          <CodeBlock code={`// Same formula appears in 4+ locations:
const rawSubtotal = order_qty * unitCost;
const discountValue = (rawSubtotal * discountPercentage) / 100;
const subtotal = (rawSubtotal - discountValue).toFixed(2);
const vat = ((subtotal * rate) / 100).toFixed(2);
const total = (parseFloat(subtotal) + parseFloat(vat)).toFixed(2);`} />
        </div>
      )}

      {/* Heatmap Tab */}
      {activeTab === 'heatmap' && (
        <div>
          <h2 style={{ color: '#d29922', marginBottom: '20px' }}>🔥 Duplication Heatmap</h2>
          
          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>By Function Type</h3>
          <div style={{ marginBottom: '30px' }}>
            <HeatmapBar label="Promotion API Logic" percent={100} />
            <HeatmapBar label="Price Calculations" percent={75} />
            <HeatmapBar label="Discount Logic" percent={60} />
            <HeatmapBar label="VAT Calculations" percent={50} />
          </div>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>By File</h3>
          <div style={{ marginBottom: '30px' }}>
            <HeatmapBar label="Quick_Order.js" percent={72} color="#f85149" />
            <HeatmapBar label="EposServices.js" percent={60} color="#d29922" />
            <HeatmapBar label="Incomplete_Order.js" percent={40} color="#79c0ff" />
          </div>

          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>Statistics</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #30363d' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>Metric</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#79c0ff' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              <TableRowStat metric="Total Redundant Lines" val="350+" />
              <TableRowStat metric="Promotion Logic Duplicates" val="3" />
              <TableRowStat metric="Price Calc Duplicates" val="4" />
              <TableRowStat metric="Code Duplication %" val="78%" />
            </tbody>
          </table>
        </div>
      )}

      {/* Fixes Tab */}
      {activeTab === 'fixes' && (
        <div>
          <h2 style={{ color: '#238636', marginBottom: '20px' }}>✅ Recommended Fixes</h2>
          
          <h3 style={{ color: '#79c0ff', marginBottom: '15px' }}>Priority 1: Critical - Fix Price Mapping Bug</h3>
          <FixBox 
            title="pdtcost Discount Check"
            effort="15 min"
            file="Incomplete_Order.js (lines 64-70)"
            action="Add discount check to pdtcost assignment"
            impact="BLOCKS other improvements"
          />

          <h3 style={{ color: '#79c0ff', marginBottom: '15px', marginTop: '20px' }}>Priority 2: High - Extract Promotion Logic</h3>
          <FixBox 
            title="Utility Function: checkProductPromotion()"
            effort="45 min"
            file="EposServices.js, Quick_Order.js (3 locations)"
            action="Extract 160+ lines of duplicated promotion check logic"
            impact="Save 120+ lines, improve maintainability"
          />

          <h3 style={{ color: '#79c0ff', marginBottom: '15px', marginTop: '20px' }}>Priority 3: High - Extract Calculation Logic</h3>
          <FixBox 
            title="Utility Function: calculatePriceWithDiscount()"
            effort="30 min"
            file="All 4 files (4 locations)"
            action="Extract 9-line calculation formula"
            impact="Save 25+ lines, reduce errors"
          />

          <h3 style={{ color: '#79c0ff', marginBottom: '15px', marginTop: '20px' }}>Priority 4: Medium - Standardize Price Fields</h3>
          <FixBox 
            title="Document & Enforce Field Naming"
            effort="1 hour"
            file="All price-related components"
            action="Standardize: price=box, piece_cost=piece, pdtcost=current"
            impact="Prevent future price swaps"
          />

          <div style={{ 
            backgroundColor: '#161b22', 
            padding: '20px', 
            borderRadius: '6px',
            marginTop: '30px',
            border: '1px solid #238636'
          }}>
            <h3 style={{ color: '#238636', margin: '0 0 15px 0' }}>Total Estimated Effort</h3>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold' }}>2-3 hours for all fixes</p>
            <p style={{ margin: '5px 0 0 0', color: '#8b949e' }}>Saves 350+ lines of duplicate code</p>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricBox({ title, value, color }) {
  return (
    <div style={{
      backgroundColor: '#161b22',
      padding: '20px',
      borderRadius: '6px',
      border: `2px solid ${color}`,
      textAlign: 'center'
    }}>
      <p style={{ margin: '0 0 10px 0', color: '#8b949e', fontSize: '12px' }}>{title}</p>
      <p style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color }}>{value}</p>
    </div>
  );
}

function TableRow({ comp, issue, impact }) {
  return (
    <tr style={{ borderBottom: '1px solid #30363d' }}>
      <td style={{ padding: '12px', color: '#c9d1d9' }}>{comp}</td>
      <td style={{ padding: '12px', color: '#f85149' }}>{issue}</td>
      <td style={{ padding: '12px', color: '#79c0ff' }}>{impact}</td>
    </tr>
  );
}

function TableRowStat({ metric, val }) {
  return (
    <tr style={{ borderBottom: '1px solid #30363d' }}>
      <td style={{ padding: '12px', color: '#8b949e' }}>{metric}</td>
      <td style={{ padding: '12px', color: '#79c0ff', fontWeight: 'bold' }}>{val}</td>
    </tr>
  );
}

function RedundancyBox({ file, lines, size }) {
  return (
    <div style={{
      backgroundColor: '#161b22',
      padding: '15px',
      borderRadius: '6px',
      border: '1px solid #30363d'
    }}>
      <p style={{ margin: '0 0 5px 0', color: '#8b949e', fontSize: '12px' }}>File</p>
      <p style={{ margin: '0 0 10px 0', color: '#c9d1d9', fontSize: '12px', fontWeight: 'bold' }}>{file}</p>
      <p style={{ margin: '0 0 5px 0', color: '#8b949e', fontSize: '12px' }}>Lines</p>
      <p style={{ margin: '0 0 10px 0', color: '#f85149', fontSize: '12px', fontWeight: 'bold' }}>{lines}</p>
      <p style={{ margin: '0', color: '#79c0ff', fontSize: '12px' }}>{size}</p>
    </div>
  );
}

function HeatmapBar({ label, percent, color = '#238636' }) {
  return (
    <div style={{ marginBottom: '15px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ color: '#8b949e', fontSize: '12px' }}>{label}</span>
        <span style={{ color: '#79c0ff', fontSize: '12px', fontWeight: 'bold' }}>{percent}%</span>
      </div>
      <div style={{
        width: '100%',
        height: '20px',
        backgroundColor: '#161b22',
        borderRadius: '4px',
        overflow: 'hidden',
        border: '1px solid #30363d'
      }}>
        <div style={{
          width: `${percent}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.3s'
        }} />
      </div>
    </div>
  );
}

function CodeBlock({ code }) {
  return (
    <pre style={{
      backgroundColor: '#0d1117',
      padding: '15px',
      borderRadius: '6px',
      border: '1px solid #30363d',
      overflow: 'auto',
      color: '#c9d1d9',
      fontSize: '12px',
      lineHeight: '1.5'
    }}>
      <code>{code}</code>
    </pre>
  );
}

function FixBox({ title, effort, file, action, impact }) {
  return (
    <div style={{
      backgroundColor: '#161b22',
      padding: '15px',
      borderRadius: '6px',
      border: '1px solid #30363d',
      marginBottom: '15px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h4 style={{ margin: '0', color: '#79c0ff' }}>{title}</h4>
        <span style={{ color: '#238636', fontWeight: 'bold', fontSize: '12px' }}>{effort}</span>
      </div>
      <p style={{ margin: '0 0 8px 0', color: '#8b949e', fontSize: '12px' }}><strong>File:</strong> {file}</p>
      <p style={{ margin: '0 0 8px 0', color: '#c9d1d9', fontSize: '12px' }}><strong>Action:</strong> {action}</p>
      <p style={{ margin: '0', color: '#79c0ff', fontSize: '12px' }}><strong>Impact:</strong> {impact}</p>
    </div>
  );
}
